/* import __COLOCATED_TEMPLATE__ from './final50-startlist-interface.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TrackedArray } from 'tracked-built-ins';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default class Final50StartlistComponent extends Component
{
	@service store;
	@service csv;
	@service sprintTools;

	@tracked semifinalsEvent;

	@tracked startTimeA;

	@tracked S1ClassGroup;
	@tracked S2ClassGroup;

	@tracked AClassGroup;

	@tracked AAssignments;

	// 4 lucky losers for 50 format finals
	@tracked LL1;
	@tracked LL2;
	@tracked LL3;
	@tracked LL4;
	@tracked LLOthers;

	@action
	async semifinalsEventChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.semifinalsEvent = null;
		}
		else
		{
			var event = await this.store.findRecord('Event', browserEvent.target.value);
			this.semifinalsEvent = event;
		}

		if(this.semifinalsEvent?.id)
		{
			await this.sprintTools.fetchResultsForEvent(this.semifinalsEvent.id);
		}
		this.S1ClassGroup = null;
		this.S2ClassGroup = null;
	}

	@action
	async S1ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S1ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S1ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}

	@action
	async S2ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S2ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S2ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}

	@action
	async AClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.AClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.startTimeA = dayjs(classGroup.startTime).format("HH:mm:ss");
			this.AClassGroup = classGroup;
		}
	}

	@action
	exportHeatStartListCSV()
	{
		if (!this.AAssignments) {
			return;
		}

		var buildData = (assignments, classGroup, startTime) => {
			var data = [...this.sprintTools.getCSVHeaders()];
			for (const result of assignments) {
				data.push(this.sprintTools.buildCSVRow(result, classGroup, startTime, result.bib));
			}
			return data;
		};

		this.csv.export(buildData(this.AAssignments, this.AClassGroup, this.startTimeA), {fileName:'A-50-finalsStartlist.csv',withSeparator:false});
	}

	@action
	async createClasses()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationClasses = await destinationEvent.get('classes');

		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.AClassGroup, this.AAssignments);
	}

	@action
	async createEntries()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationEntries = await destinationEvent.get('entries');
		var destinationClasses = await destinationEvent.get('classes');

		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.AClassGroup, this.AAssignments, this.startTimeA, false);
	}

	async calculateLuckyLosers()
	{
		var S1Results = [...(await this.S1ClassGroup.resultsSorted)];
		var S2Results = [...(await this.S2ClassGroup.resultsSorted)];

		// Collect 4th-7th place finishers from both SFs as lucky loser candidates
		var luckyLoserCandidates = [];

		var addCandidate = function(result) {
			if(result && result.status == 'OK' && result.totalTimeInMilliseconds != null) {
				luckyLoserCandidates.push(result);
			}
		};

		// 4th place (index 3) from each SF
		addCandidate(S1Results[3]);
		addCandidate(S2Results[3]);

		// 5th place (index 4) from each SF
		addCandidate(S1Results[4]);
		addCandidate(S2Results[4]);

		// 6th place (index 5) from each SF
		addCandidate(S1Results[5]);
		addCandidate(S2Results[5]);

		// 7th place (index 6) from each SF
		addCandidate(S1Results[6]);
		addCandidate(S2Results[6]);

		// Sort by time (fastest first)
		luckyLoserCandidates.sort((a, b) => a.totalTimeInMilliseconds - b.totalTimeInMilliseconds);

		// Select top 4 as lucky losers
		this.LL1 = luckyLoserCandidates[0] || null;
		this.LL2 = luckyLoserCandidates[1] || null;
		this.LL3 = luckyLoserCandidates[2] || null;
		this.LL4 = luckyLoserCandidates[3] || null;

		// Rest go to unlucky losers
		var LLOthers = new TrackedArray();
		for(var i = 4; i < luckyLoserCandidates.length; i++) {
			LLOthers.push(luckyLoserCandidates[i]);
		}
		// Also add any remaining 8th+ place finishers
		if(S1Results[7]) LLOthers.push(S1Results[7]);
		if(S2Results[7]) LLOthers.push(S2Results[7]);
		if(S1Results[8]) LLOthers.push(S1Results[8]);
		if(S2Results[8]) LLOthers.push(S2Results[8]);
		if(S1Results[9]) LLOthers.push(S1Results[9]);
		if(S2Results[9]) LLOthers.push(S2Results[9]);

		this.LLOthers = LLOthers;
	}

	async calculateHeatAssignments()
	{
		var AAssignments = [];

		var S1ClassGroup = this.S1ClassGroup;
		var S2ClassGroup = this.S2ClassGroup;

		if(	S1ClassGroup == null ||
			S2ClassGroup == null)
		{
			return;
		}

		await this.calculateLuckyLosers();
		var S1Results = [...(await S1ClassGroup.resultsSorted)];
		var S2Results = [...(await S2ClassGroup.resultsSorted)];

		// A Final: S1.1, S2.1, S1.2, S2.2, (S1.3/S2.3 by time), LL1, LL2, LL3, LL4
		// Fixed table order - only S1.3/S2.3 sorted by semifinal time

		// Positions 1-4: Fixed order (no bib sorting)
		S1Results[0].set('heatStartOrder', 1);  // S1.1
		AAssignments.push(S1Results[0]);

		S2Results[0].set('heatStartOrder', 2);  // S2.1
		AAssignments.push(S2Results[0]);

		S1Results[1].set('heatStartOrder', 3);  // S1.2
		AAssignments.push(S1Results[1]);

		S2Results[1].set('heatStartOrder', 4);  // S2.2
		AAssignments.push(S2Results[1]);

		// Positions 5-6: S1.3 and S2.3 (sorted by semifinal TIME - faster first)
		var S1_3 = S1Results[2];
		var S2_3 = S2Results[2];
		if(S1_3.totalTimeInMilliseconds < S2_3.totalTimeInMilliseconds)
		{
			S1_3.set('heatStartOrder', 5);
			S2_3.set('heatStartOrder', 6);
			AAssignments.push(S1_3);
			AAssignments.push(S2_3);
		}
		else
		{
			S2_3.set('heatStartOrder', 5);
			S1_3.set('heatStartOrder', 6);
			AAssignments.push(S2_3);
			AAssignments.push(S1_3);
		}

		// Positions 7-10: Lucky losers (LL1, LL2, LL3, LL4)
		var startOrder = 7;
		if(this.LL1) {
			this.LL1.set('heatStartOrder', startOrder++);
			AAssignments.push(this.LL1);
		}
		if(this.LL2) {
			this.LL2.set('heatStartOrder', startOrder++);
			AAssignments.push(this.LL2);
		}
		if(this.LL3) {
			this.LL3.set('heatStartOrder', startOrder++);
			AAssignments.push(this.LL3);
		}
		if(this.LL4) {
			this.LL4.set('heatStartOrder', startOrder++);
			AAssignments.push(this.LL4);
		}

		this.AAssignments = AAssignments;
	}
}
